import type { OrderStatus } from "@/shared/types";

/**
 * The one colour per order status, for the whole app.
 *
 * It lives in `shared` rather than in `entities/order` because BOTH
 * `entities/order` (the orders table's status pills) and `entities/analytics`
 * (the status charts) need it, and FSD forbids one entity importing another.
 * `OrderStatus` itself is already in shared/types, so this sits beside it.
 *
 * ── How the two old maps were reconciled ─────────────────────────────────
 *
 * There used to be two: this set of values inline in `OrderList`, and
 * `STATUS_COLORS` in `entities/analytics/model/mappers.ts` driving the charts.
 * They disagreed on three statuses, so a vendor saw the same order state in
 * different colours depending on the screen. Resolved as:
 *
 *   PENDING          #f97316 → #EF9F27   analytics (deliberate amber)
 *   PROCESSING       #0f766e → #378ADD   analytics — the old teal was the
 *                                        pre-rebrand brand colour
 *   SHIPPED          #7c3aed → #5DCAA5   analytics
 *   DELIVERED        #10b981 → #1D9E75   analytics
 *   RETURNED         #8b5cf6 → #7c3aed   analytics
 *   DELIVERY_FAILED  #dc2626             both already agreed
 *   CONFIRMED        #2563eb             only the orders map had it
 *   CANCELLED        #ef4444 KEPT RED    deliberately NOT the analytics
 *                                        #D85A30 — orange reads weaker than
 *                                        red for a negative terminal state.
 *                                        DELIVERY_FAILED is a darker red, so
 *                                        the two stay distinguishable.
 *
 * The analytics chart palette reads `color` from here; the status pills read
 * both. Change a hue once, here.
 */
export interface OrderStatusColor {
  /** Text and border colour for the status pill; series colour in a chart. */
  color: string;
  /** Soft fill behind the pill. */
  bgColor: string;
}

const ORDER_STATUS_COLORS: Record<OrderStatus, OrderStatusColor> = {
  PENDING: { color: "#EF9F27", bgColor: "#fff7ed" },
  CONFIRMED: { color: "#2563eb", bgColor: "#eff6ff" },
  PROCESSING: { color: "#378ADD", bgColor: "#dbeafe" },
  SHIPPED: { color: "#5DCAA5", bgColor: "#d1fae5" },
  DELIVERED: { color: "#1D9E75", bgColor: "#f0fdf4" },
  CANCELLED: { color: "#ef4444", bgColor: "#fef2f2" },
  DELIVERY_FAILED: { color: "#dc2626", bgColor: "#fef2f2" },
  RETURNED: { color: "#7c3aed", bgColor: "#faf5ff" },
};

/** Neutral grey — an unrecognised status still needs to render. */
const FALLBACK: OrderStatusColor = { color: "#9ca3af", bgColor: "#f3f4f6" };

/** Case-insensitive; unknown statuses fall back to neutral grey. */
export function orderStatusColor(status: string | undefined): OrderStatusColor {
  const key = (status ?? "").toString().toUpperCase() as OrderStatus;
  return ORDER_STATUS_COLORS[key] ?? FALLBACK;
}
